import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ChevronDown, ChevronRight, GitBranch, Square } from 'lucide-react'

import { T } from '../../copy/text'
import type { ModelOption } from './model-picker'
import type { WorkItemNode } from './panels'
import { resolveDisplayName, type SubAgentRun, type TeamMember } from './roster'
import {
  ITEM_STATE_FACTS,
  projectItemState,
  type ItemState,
  type LinkState,
  type Tone,
} from './session-state'
import { SubFailExits } from './sidecar-panels'
import type { SubFailExitKind } from './subfail'
import { buildWorkflowRunSummary, isDoneRun, isTerminalRun, mayCancelRun } from './run-state'

const TONE_DOT: Record<Tone, string> = {
  n: 'bg-npc-lulu-t4',
  g: 'bg-npc-lulu-ok',
  w: 'bg-npc-lulu-warn',
  r: 'bg-npc-lulu-bad',
  a: 'bg-npc-lulu-t2',
}

const TONE_TEXT: Record<Tone, string> = {
  n: 'text-npc-lulu-t3',
  g: 'text-npc-lulu-ok',
  w: 'text-npc-lulu-warn',
  r: 'text-npc-lulu-bad',
  a: 'text-npc-lulu-t1',
}

export function WorkflowSummaryCard({
  items,
  link,
  onOpen,
}: {
  items: readonly WorkItemNode[]
  link: LinkState
  onOpen: () => void
}) {
  const summary = buildWorkflowRunSummary(items)
  if (summary.agentCount === 0) return null
  const running = summary.activeCount > 0 && link === 'live'

  return (
    <button
      type="button"
      data-testid="lulu-workflow-summary"
      onClick={onOpen}
      className="group w-full rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 py-2.5 text-left hover:border-npc-lulu-t4 hover:bg-npc-lulu-bg-3"
    >
      <span className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          data-animate={running ? 'yes' : 'no'}
          className={[
            'mt-1 flex size-7 shrink-0 items-center justify-center rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 text-npc-lulu-t3',
            running ? 'animate-npc-pulse' : '',
          ].join(' ')}
        >
          <GitBranch size={14} strokeWidth={1.8} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-npc-caption font-medium text-npc-lulu-t1">
            {summary.title}
          </span>
          <span className="mt-0.5 block text-npc-meta text-npc-lulu-t3">
            <T
              id="lulu_workbench.run.summary.meta"
              vars={{
                phases: String(summary.phaseCount),
                agents: String(summary.agentCount),
                done: String(summary.doneCount),
              }}
            />
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-npc-meta text-npc-lulu-t3 group-hover:text-npc-lulu-t1">
          <T id="lulu_workbench.run.summary.open" />
          <ChevronRight size={14} aria-hidden="true" />
        </span>
      </span>
    </button>
  )
}

function StateBadge({ state, link }: { state: ItemState | null; link: LinkState }) {
  const projected = projectItemState(state, link)
  const facts = ITEM_STATE_FACTS[projected]
  return (
    <span
      data-testid="lulu-run-state"
      data-state={projected}
      data-animate={facts.mayAnimate ? 'yes' : 'no'}
      className={[
        'inline-flex shrink-0 items-center gap-1 text-npc-meta',
        TONE_TEXT[facts.tone],
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'size-1.5 rounded-full',
          TONE_DOT[facts.tone],
          facts.mayAnimate ? 'animate-npc-pulse' : '',
        ].join(' ')}
      />
      <T id={facts.copyId} />
    </span>
  )
}

function durationOf(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '—'
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`
}

function memberForRun(run: SubAgentRun, team: readonly TeamMember[] | null) {
  if (!run.agentProfileId || team === null) return null
  return team.find((member) => member.id === run.agentProfileId) ?? null
}

function displayNameOf(
  run: SubAgentRun,
  team: readonly TeamMember[] | null,
  responsibility: string,
): string {
  const member = memberForRun(run, team)
  return resolveDisplayName(member ?? run) ?? responsibility
}

function modelOf(run: SubAgentRun, team: readonly TeamMember[] | null): string {
  return run.modelId ?? memberForRun(run, team)?.modelId ?? '—'
}

function parentNameOf(run: SubAgentRun, team: readonly TeamMember[] | null): string {
  if (!run.parentAgentId || team === null) return '—'
  const parent = team.find((member) => member.id === run.parentAgentId)
  return parent ? (resolveDisplayName(parent) ?? '—') : '—'
}

function AgentRow({
  run,
  responsibility,
  team,
  link,
  onOpen,
}: {
  run: SubAgentRun
  responsibility: string
  team: readonly TeamMember[] | null
  link: LinkState
  onOpen: () => void
}) {
  const model = modelOf(run, team)
  const elapsed = durationOf(run.elapsedMs)
  return (
    <button
      type="button"
      data-testid="lulu-run-agent-row"
      onClick={onOpen}
      className="grid w-full grid-cols-[minmax(0,1fr)_88px_58px_62px] items-center gap-2 border-t border-npc-lulu-line-1 px-2 py-2 text-left hover:bg-npc-lulu-bg-3 max-[600px]:grid-cols-[minmax(0,1fr)_auto]"
    >
      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-npc-caption font-medium text-npc-lulu-t1">
            {displayNameOf(run, team, responsibility)}
          </span>
          <StateBadge state={run.state} link={link} />
        </span>
        <span className="mt-0.5 block truncate text-npc-meta text-npc-lulu-t4">
          {responsibility}
        </span>
        <span className="mt-1 hidden truncate font-npc-mono text-npc-meta text-npc-lulu-t4 max-[600px]:block">
          {model} · — · {elapsed}
        </span>
      </span>
      <span className="truncate font-npc-mono text-npc-meta text-npc-lulu-t3 max-[600px]:hidden">
        {model}
      </span>
      <span className="font-npc-mono text-npc-meta text-npc-lulu-t4 max-[600px]:hidden">—</span>
      <span className="font-npc-mono text-npc-meta text-npc-lulu-t4 max-[600px]:hidden">
        {elapsed}
      </span>
      <ChevronRight
        className="hidden text-npc-lulu-t4 max-[600px]:block"
        size={16}
        aria-hidden="true"
      />
    </button>
  )
}

/**
 * 三种叫停，**三句话、三个后果**，所以是三档而不是一个布尔。
 *
 *	one    停一个子 Agent           这一轮还在跑
 *	all    逐条停掉在跑的子 Agent   这一轮还在跑（没有子 Agent 的活一动不动）
 *	round  停下这一轮               这一轮结束了
 *
 * `all` 与 `round` 看起来最像，而它们差的正是那些**没有子 Agent 的活**：
 * 刚派下去的、blocked 的，在板上占一行而 `subRuns` 是空的。
 * 合成一档的话，用户点完「停止全部」会看到那几行还在——
 * 而他刚刚被告知都停了。
 */
type Confirmation = { readonly kind: 'one' | 'all' | 'round'; readonly ids: readonly string[] }

const CONFIRM_COPY = {
  one: 'lulu_workbench.run.action.confirm_one',
  all: 'lulu_workbench.run.action.confirm_all',
  round: 'lulu_workbench.run.action.confirm_round',
} as const

// 失败那句也按档分：「这一条没停下来」对整轮叫停是假的。
const CONFIRM_FAILED_COPY = {
  one: 'lulu_workbench.run.action.failed',
  all: 'lulu_workbench.run.action.failed',
  round: 'lulu_workbench.run.action.round_failed',
} as const

export function RunBoardPanel({
  items,
  team,
  link,
  onCancelSubrun,
  onStopRound,
  onRecoverSubrun,
  onUndo,
  swapCandidates,
  paused,
  onPause,
  onResume,
}: {
  items: readonly WorkItemNode[]
  team: readonly TeamMember[] | null
  link: LinkState
  onCancelSubrun?: (subrunId: string) => Promise<void>
  /**
   * 停下这一轮。回的是**服务端说停了几件**，不是这一侧数出来的——
   * 两处分家的表现是「界面说停了 3 件、实际停了 1 件」，而没有东西会红。
   *
   * **不给的话按钮不出现**，与 onUndo 同一条：一颗点不动的「停下这一轮」
   * 比没有按钮更糟，它让人以为停得掉。
   */
  /** 工作板那颗「停下这一轮」：停这条话头里所有没做完的活（scope=all）。 */
  onStopRound?: (scope: 'all' | 'turn') => Promise<{ readonly cancelledItems: number }>
  onRecoverSubrun?: (subrunId: string, kind: SubFailExitKind, adapter?: string) => Promise<void>
  /**
   * 撤回这次安排。**不给的话按钮不出现**——一个点不动的撤回按钮
   * 比没有按钮更糟：它让人以为撤得掉。
   */
  onUndo?: (taskId: string) => Promise<void>
  swapCandidates: readonly ModelOption[] | null
  /** 这条话头暂停着没有。暂停不是停下：话照说，只是拆出来的计划不开工、没领走的活不派。 */
  paused?: boolean
  onPause?: () => Promise<void>
  onResume?: () => Promise<void>
}) {
  const summary = buildWorkflowRunSummary(items)
  const firstOpen = items.find((item) => !isTerminalRun(item.state))?.id ?? items[0]?.id ?? null
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(firstOpen ? [firstOpen] : []))
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  // 暂停 / 继续：按下去就灰，回来再亮；失败不吞——按钮回到原样，状态以服务端为准。
  const [pausing, setPausing] = useState(false)
  const togglePause = async (fn: () => Promise<void>) => {
    setPausing(true)
    try {
      await fn()
    } catch {
      // 这颗按钮没有自己的错误行：失败就回到原样，他会再点。
    } finally {
      setPausing(false)
    }
  }
  const [stopping, setStopping] = useState(false)
  const [stopFailed, setStopFailed] = useState(false)
  const rememberedScroll = useRef(0)

  useEffect(() => {
    if (items.length === 0) return
    setExpanded((current) => {
      if ([...current].some((id) => items.some((item) => item.id === id))) return current
      return new Set(firstOpen ? [firstOpen] : [])
    })
  }, [firstOpen, items])

  const selected = useMemo(() => {
    for (const item of items) {
      const run = item.subRuns.find((candidate) => candidate.id === selectedRunId)
      if (run) return { item, run }
    }
    return null
  }, [items, selectedRunId])

  const cancellable = items.flatMap((item) => item.subRuns).filter((run) => mayCancelRun(run.state))
  // **这一轮还有没有没做完的活，判据在工作包上**，不在子 Agent 上：
  // 一件刚派下去或者 blocked 的活 subRuns 是空的，而它照样在这一轮里。
  // 用 cancellable.length 当判据的话，那种活在的时候按钮压根不出现。
  const roundHasLiveWork = items.some((item) => !isTerminalRun(item.state))
  const [roundStopped, setRoundStopped] = useState<number | null>(null)
  const [undoing, setUndoing] = useState<string | null>(null)
  const [undoAsk, setUndoAsk] = useState<string | null>(null)
  const [undoNote, setUndoNote] = useState<'done' | 'failed' | null>(null)

  const openDetail = (runId: string) => {
    rememberedScroll.current =
      document.querySelector<HTMLElement>('[data-testid="lulu-sidecar-panel"]')?.scrollTop ?? 0
    setSelectedRunId(runId)
  }

  const closeDetail = () => {
    setSelectedRunId(null)
    requestAnimationFrame(() => {
      const panel = document.querySelector<HTMLElement>('[data-testid="lulu-sidecar-panel"]')
      if (panel) panel.scrollTop = rememberedScroll.current
    })
  }

  const confirmStop = async () => {
    if (!confirmation) return
    if (confirmation.kind === 'round' ? !onStopRound : !onCancelSubrun) return
    setStopping(true)
    setStopFailed(false)
    try {
      if (confirmation.kind === 'round') {
        // 记的是**服务端说停了几件**。这一侧数板上有几行会在两处分家的
        // 那天说假话，而那天不会有任何东西红。
        const out = await onStopRound!('all')
        setRoundStopped(out.cancelledItems)
      } else {
        for (const id of confirmation.ids) await onCancelSubrun!(id)
      }
      setConfirmation(null)
    } catch {
      setStopFailed(true)
    } finally {
      setStopping(false)
    }
  }

  if (selected) {
    const { item, run } = selected
    const parent = parentNameOf(run, team)
    return (
      <div data-testid="lulu-run-detail" className="min-w-0">
        <button
          type="button"
          data-testid="lulu-run-detail-back"
          onClick={closeDetail}
          className="mb-3 inline-flex min-h-10 items-center gap-1.5 rounded-npc-chip px-1 text-npc-caption text-npc-lulu-t2 hover:text-npc-lulu-t1"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          <T id="lulu_workbench.run.detail.back" />
        </button>

        <div className="border-b border-npc-lulu-line-1 pb-3">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="truncate font-npc-display text-npc-section text-npc-lulu-t1">
                {displayNameOf(run, team, item.name)}
              </h3>
              <p className="mt-1 truncate font-npc-mono text-npc-meta text-npc-lulu-t4">
                {modelOf(run, team)}
              </p>
            </div>
            <StateBadge state={run.state} link={link} />
          </div>
        </div>

        <dl className="divide-y divide-npc-lulu-line-1 border-b border-npc-lulu-line-1">
          <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 py-3">
            <dt className="text-npc-meta text-npc-lulu-t4">
              <T id="lulu_workbench.run.detail.responsibility" />
            </dt>
            <dd className="text-npc-caption text-npc-lulu-t1">{item.name}</dd>
          </div>
          <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 py-3">
            <dt className="text-npc-meta text-npc-lulu-t4">
              <T id="lulu_workbench.sub.parent" />
            </dt>
            <dd className="text-npc-caption text-npc-lulu-t1">{parent}</dd>
          </div>
          <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 py-3">
            <dt className="text-npc-meta text-npc-lulu-t4">
              <T id="lulu_workbench.run.column.token" />
            </dt>
            <dd className="font-npc-mono text-npc-caption text-npc-lulu-t1">—</dd>
          </div>
          <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 py-3">
            <dt className="text-npc-meta text-npc-lulu-t4">
              <T id="lulu_workbench.run.column.elapsed" />
            </dt>
            <dd className="font-npc-mono text-npc-caption text-npc-lulu-t1">
              {durationOf(run.elapsedMs)}
            </dd>
          </div>
        </dl>

        <p className="py-3 text-npc-meta leading-5 text-npc-lulu-t3">
          <T id="lulu_workbench.run.detail.evidence" />
        </p>

        {run.state === 'fail' ? (
          <div className="border-t border-npc-lulu-line-1 pt-3">
            <SubFailExits
              subrunId={run.id}
              currentAdapter={run.modelId}
              alternatives={swapCandidates}
              onRecover={
                onRecoverSubrun
                  ? async (kind, adapter) => onRecoverSubrun(run.id, kind, adapter)
                  : undefined
              }
            />
          </div>
        ) : null}

        {mayCancelRun(run.state) ? (
          <button
            type="button"
            data-testid="lulu-run-stop-one"
            disabled={!onCancelSubrun || stopping}
            onClick={() => setConfirmation({ kind: 'one', ids: [run.id] })}
            className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-npc-chip border border-npc-lulu-line-2 px-3 text-npc-caption text-npc-lulu-t2 disabled:opacity-50"
          >
            <Square size={13} fill="currentColor" aria-hidden="true" />
            <T id="lulu_workbench.run.action.stop" />
          </button>
        ) : null}

        <ConfirmationBar
          confirmation={confirmation}
          stopping={stopping}
          failed={stopFailed}
          onConfirm={() => void confirmStop()}
          onCancel={() => setConfirmation(null)}
        />
      </div>
    )
  }

  return (
    <div data-testid="lulu-run-board" className="min-w-0">
      <section className="border-b border-npc-lulu-line-1 pb-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-npc-display text-npc-section text-npc-lulu-t1">{summary.title}</h3>
            <p className="mt-1 text-npc-meta text-npc-lulu-t3">
              <T
                id="lulu_workbench.run.meta"
                vars={{ phases: String(summary.phaseCount), agents: String(summary.agentCount) }}
              />
            </p>
          </div>
          {/*
            两颗按钮在 390 宽的手机上要么并排放得下、要么换行——
            **不许把标题挤没**。所以这里是一个 shrink-0 的 flex-wrap 容器，
            标题那一侧 min-w-0 自己截断。
          */}
          <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
            {paused ? (
              <span
                data-testid="lulu-run-paused"
                className="inline-flex min-h-10 items-center rounded-npc-chip bg-npc-lulu-warn/10 px-2.5 text-npc-meta text-npc-lulu-warn"
              >
                <T id="lulu_workbench.run.paused" />
              </span>
            ) : null}
            {paused ? (
              onResume ? (
                <button
                  type="button"
                  data-testid="lulu-run-resume"
                  disabled={pausing}
                  onClick={() => void togglePause(onResume)}
                  className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-npc-chip border border-npc-lulu-line-2 px-2.5 text-npc-meta text-npc-lulu-t2 disabled:opacity-50"
                >
                  <T id="lulu_workbench.run.action.resume" />
                </button>
              ) : null
            ) : onPause ? (
              <button
                type="button"
                data-testid="lulu-run-pause"
                disabled={pausing}
                onClick={() => void togglePause(onPause)}
                className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-npc-chip border border-npc-lulu-line-2 px-2.5 text-npc-meta text-npc-lulu-t2 disabled:opacity-50"
              >
                <T id="lulu_workbench.run.action.pause" />
              </button>
            ) : null}
            {cancellable.length > 0 ? (
              <button
                type="button"
                data-testid="lulu-run-stop-all"
                disabled={!onCancelSubrun || stopping}
                onClick={() =>
                  setConfirmation({ kind: 'all', ids: cancellable.map((run) => run.id) })
                }
                className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-npc-chip border border-npc-lulu-line-2 px-2.5 text-npc-meta text-npc-lulu-t2 disabled:opacity-50"
              >
                <Square size={11} fill="currentColor" aria-hidden="true" />
                <T id="lulu_workbench.run.action.stop_all" />
              </button>
            ) : null}
            {/*
              **判据是工作包，不是子 Agent**：一件刚派下去或者 blocked 的活
              subRuns 是空的，而它照样在这一轮里。用 cancellable 当判据的话，
              恰恰是最需要叫停的那种局面（派不下去、卡着）按钮不出现。
            */}
            {onStopRound && roundHasLiveWork ? (
              <button
                type="button"
                data-testid="lulu-run-stop-round"
                disabled={stopping}
                onClick={() => setConfirmation({ kind: 'round', ids: [] })}
                className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-npc-chip border border-npc-lulu-line-2 px-2.5 text-npc-meta text-npc-lulu-t2 disabled:opacity-50"
              >
                <Square size={11} fill="currentColor" aria-hidden="true" />
                <T id="lulu_workbench.run.action.stop_round" />
              </button>
            ) : null}
          </div>
        </div>
        {/*
          停完之后说一句他能核对的话。
          **0 件与「停了几件」是两句**：前者他不用去看，后者他会去看停掉了什么。
        */}
        {roundStopped === null ? null : (
          <p
            data-testid="lulu-run-round-note"
            className="mt-2 text-npc-meta leading-5 text-npc-lulu-t3"
          >
            {roundStopped > 0 ? (
              <T
                id="lulu_workbench.run.action.round_stopped"
                vars={{ count: String(roundStopped) }}
              />
            ) : (
              <T id="lulu_workbench.run.action.round_already_stopped" />
            )}
          </p>
        )}
      </section>

      {/*
        **空态的判据是「一个工作包都没有」，不是「一个子 Agent 都没在跑」。**

        原来这里还有一条 `summary.agentCount === 0`，而 agentCount 数的是
        子 run 的总数（run-state.ts:35）。后果是：一个刚派下去、还没开出子 Agent
        的工作包，和一个 blocked 的工作包（它一个子 run 都不会有），
        **在板上完全看不见**——整块渲染成「暂无」。

        2026-08-29 撞到的：给 blocked 的包加「卡住的原因」那一行时，
        带子 run 的用例过、不带的不过。而 no_eligible_executor 的包恰恰
        一个子 run 都没有，也就是说那句话在生产上永远不会显示。

        一个存在的工作包是真实的 durable 记录（有名字、有状态、有归属），
        按八条产品合同第 3 条它就该显示。**没有子 Agent 不等于没有事情**。
      */}
      {items.length === 0 ? (
        <p data-testid="lulu-run-empty" className="py-5 text-npc-caption text-npc-lulu-t3">
          <T id="lulu_workbench.run.empty" />
        </p>
      ) : (
        <div className="divide-y divide-npc-lulu-line-1">
          {items.map((item) => {
            const open = expanded.has(item.id)
            const activeRuns = item.subRuns.filter((run) => !isTerminalRun(run.state))
            const finishedRuns = item.subRuns.filter((run) => isTerminalRun(run.state))
            const done = item.subRuns.filter((run) => isDoneRun(run.state)).length
            return (
              <section key={item.id} data-testid="lulu-run-phase" className="py-1.5">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => {
                    setExpanded((current) => {
                      const next = new Set(current)
                      if (next.has(item.id)) next.delete(item.id)
                      else next.add(item.id)
                      return next
                    })
                  }}
                  className="flex min-h-11 w-full items-center gap-2 px-1 text-left"
                >
                  {open ? (
                    <ChevronDown size={15} aria-hidden="true" />
                  ) : (
                    <ChevronRight size={15} aria-hidden="true" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-npc-caption font-medium text-npc-lulu-t1">
                    {item.name}
                  </span>
                  <span className="font-npc-mono text-npc-meta text-npc-lulu-t4">
                    <T
                      id="lulu_workbench.run.phase.count"
                      vars={{ done: String(done), total: String(item.subRuns.length) }}
                    />
                  </span>
                  <StateBadge state={item.state} link={link} />
                </button>

                {/*
                  卡住的原因，**后端渲染好的那句原样显示**。
                  没有就不画这一行——一个空的原因框和一个真的说不出原因的
                  状态在屏幕上是同一个东西，而它们说的是两件事。

                  它不与状态标签合并：那个标签说的是「卡住了」，
                  这一句说的是「你接下来该做什么」。
                */}
                {item.blockedReason ? (
                  <p
                    data-testid="lulu-work-item-blocked-reason"
                    className="border-t border-npc-lulu-line-1 px-2 py-2 text-npc-meta text-npc-lulu-t3"
                  >
                    {item.blockedReason}
                  </p>
                ) : null}

                {/*
                  撤回。**只有真的撤得掉时才出现**：要有那条任务（非
                  development 的步骤没有），要有接线（onUndo），
                  而且这一步还没走到终态。
                  一个点不动的撤回按钮比没有按钮更糟——它让人以为撤得掉。
                */}
                {onUndo && item.taskId && !isTerminalRun(item.state) ? (
                  <div className="border-t border-npc-lulu-line-1 px-2 py-2">
                    {undoAsk === item.taskId ? (
                      <div className="flex flex-col gap-2">
                        <p className="text-npc-meta text-npc-lulu-t3">
                          <T id="lulu_workbench.board.undo_confirm" />
                        </p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            data-testid="lulu-board-undo-confirm"
                            disabled={undoing === item.taskId}
                            onClick={async () => {
                              const id = item.taskId
                              if (!id) return
                              setUndoing(id)
                              setUndoNote(null)
                              try {
                                await onUndo(id)
                                setUndoNote('done')
                                setUndoAsk(null)
                              } catch {
                                setUndoNote('failed')
                              } finally {
                                setUndoing(null)
                              }
                            }}
                            className="rounded-npc-chip border border-npc-lulu-line-2 px-2.5 py-1 text-npc-meta text-npc-lulu-t1 hover:bg-npc-lulu-bg-3 disabled:opacity-50"
                          >
                            <T id="lulu_workbench.board.undo" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setUndoAsk(null)}
                            className="rounded-npc-chip px-2.5 py-1 text-npc-meta text-npc-lulu-t4 hover:bg-npc-lulu-bg-3"
                          >
                            <T id="lulu_workbench.run.action.cancel" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        data-testid="lulu-board-undo"
                        onClick={() => {
                          setUndoAsk(item.taskId)
                          setUndoNote(null)
                        }}
                        className="text-npc-meta text-npc-lulu-t4 hover:text-npc-lulu-t1"
                      >
                        <T id="lulu_workbench.board.undo" />
                      </button>
                    )}
                    {undoNote && undoAsk !== item.taskId ? (
                      <p data-testid="lulu-board-undo-note" className="mt-1 text-npc-meta text-npc-lulu-t3">
                        <T
                          id={
                            undoNote === 'done'
                              ? 'lulu_workbench.board.undo_done'
                              : 'lulu_workbench.board.undo_failed'
                          }
                        />
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {open ? (
                  <div className="overflow-hidden rounded-npc-chip border border-npc-lulu-line-1 bg-npc-lulu-bg-2">
                    <div className="grid grid-cols-[minmax(0,1fr)_88px_58px_62px] gap-2 px-2 py-1.5 text-npc-meta text-npc-lulu-t4 max-[600px]:hidden">
                      <T id="lulu_workbench.run.column.agent" />
                      <T id="lulu_workbench.run.column.model" />
                      <T id="lulu_workbench.run.column.token" />
                      <T id="lulu_workbench.run.column.elapsed" />
                    </div>
                    {activeRuns.map((run) => (
                      <AgentRow
                        key={run.id}
                        run={run}
                        responsibility={item.name}
                        team={team}
                        link={link}
                        onOpen={() => openDetail(run.id)}
                      />
                    ))}
                    {finishedRuns.length > 0 ? (
                      <details
                        data-testid="lulu-run-finished"
                        className="border-t border-npc-lulu-line-1"
                      >
                        <summary className="flex min-h-10 cursor-pointer list-none items-center gap-2 px-2 text-npc-meta text-npc-lulu-t3">
                          <ChevronRight size={14} aria-hidden="true" />
                          <T
                            id="lulu_workbench.run.heading.finished"
                            vars={{ count: String(finishedRuns.length) }}
                          />
                        </summary>
                        {finishedRuns.map((run) => (
                          <AgentRow
                            key={run.id}
                            run={run}
                            responsibility={item.name}
                            team={team}
                            link={link}
                            onOpen={() => openDetail(run.id)}
                          />
                        ))}
                      </details>
                    ) : null}
                  </div>
                ) : null}
              </section>
            )
          })}
        </div>
      )}

      <ConfirmationBar
        confirmation={confirmation}
        stopping={stopping}
        failed={stopFailed}
        onConfirm={() => void confirmStop()}
        onCancel={() => setConfirmation(null)}
      />

      <p className="border-t border-npc-lulu-line-1 pt-3 text-npc-meta leading-5 text-npc-lulu-t3">
        <T id="lulu_workbench.run.footer" />
      </p>
    </div>
  )
}

function ConfirmationBar({
  confirmation,
  stopping,
  failed,
  onConfirm,
  onCancel,
}: {
  confirmation: Confirmation | null
  stopping: boolean
  failed: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  if (!confirmation) return null
  return (
    <div
      data-testid="lulu-run-confirm"
      className="mt-3 border-l-2 border-npc-lulu-warn bg-npc-lulu-bg-2 px-3 py-2.5"
    >
      <p className="text-npc-caption text-npc-lulu-t1">
        <T id={CONFIRM_COPY[confirmation.kind]} />
      </p>
      {failed ? (
        <p className="mt-1 text-npc-meta text-npc-lulu-bad">
          <T id={CONFIRM_FAILED_COPY[confirmation.kind]} />
        </p>
      ) : null}
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={stopping}
          onClick={onConfirm}
          className="min-h-10 rounded-npc-chip bg-npc-lulu-t1 px-3 text-npc-caption text-npc-lulu-bg-0 disabled:opacity-50"
        >
          <T id="lulu_workbench.run.action.confirm" />
        </button>
        <button
          type="button"
          disabled={stopping}
          onClick={onCancel}
          className="min-h-10 rounded-npc-chip border border-npc-lulu-line-2 px-3 text-npc-caption text-npc-lulu-t2 disabled:opacity-50"
        >
          <T id="lulu_workbench.run.action.cancel" />
        </button>
      </div>
    </div>
  )
}
